import { FightEvents, FightTurn } from '../components/interfaces/Interfaces';

interface Role {
  index: number;
  isOnOffense: boolean;
  displayName?: string;
}
interface Battle {
  roles?: Role[];
  fight?: FightTurn[];
  victory?: boolean;
  tie?: boolean;
}

/** Summarize the damage numbers recorded in the replay, without rerunning combat. */
export function getBattleSummary(battle: Battle | null, playerName?: string) {
  const summary = {
    damageDealt: 0, damageTaken: 0, criticalHits: 0, criticalDamage: 0,
    reflectedTaken: 0, healing: 0, highestHit: 0, hits: 0, misses: 0, seconds: 0,
    result: battle?.tie ? 'draw' : battle?.victory === true ? 'win' : battle?.victory === false ? 'loss' : 'finished',
  };
  const roles = Array.isArray(battle?.roles) ? battle!.roles! : [];
  const turns = Array.isArray(battle?.fight) ? battle!.fight! : [];
  const player = roles.find(role => role.isOnOffense && playerName && role.displayName === playerName)
    ?? roles.find(role => role.isOnOffense);
  if (!player) return summary;
  const byIndex = new Map(roles.map(role => [role.index, role]));
  const sides = [roles.filter(role => role.isOnOffense), roles.filter(role => !role.isOnOffense)];
  const active = [0, 0];
  const amount = (value: unknown) => typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0;

  for (const turn of turns) {
    summary.seconds = Math.max(summary.seconds, amount(turn.time) / 100);
    const originalSource = byIndex.get(turn.role);
    if (!originalSource) continue;
    const side = originalSource.isOnOffense ? 0 : 1;
    const originalTarget = byIndex.get(turn.targetRole as number) ?? sides[1 - side][active[1 - side]];
    // A kicked bomb damages the thrower; the recorded role remains the thrower.
    const source = turn.event === FightEvents.BACK_THROWED ? originalTarget : originalSource;
    const target = turn.event === FightEvents.BACK_THROWED ? originalSource : originalTarget;
    const direct = turn.isHit === false ? 0 : amount(turn.targetLastDamage);
    const reflected = amount(turn.reflectedDamage);
    const self = amount(turn.selfLastDamage);

    if (source?.index === player.index) {
      if (target && target.isOnOffense !== player.isOnOffense) {
        summary.damageDealt += direct;
        summary.highestHit = Math.max(summary.highestHit, direct);
        if (direct > 0) {
          summary.hits++;
          if (turn.isCrit) { summary.criticalHits++; summary.criticalDamage += direct; }
        }
        if (turn.isHit === false) summary.misses++;
      }
      summary.damageTaken += reflected + self;
      summary.reflectedTaken += reflected;
      summary.healing += amount(turn.incHp) + amount(-(turn.selfLastDamage ?? 0));
    }
    if (target?.index === player.index && source?.isOnOffense !== player.isOnOffense) {
      summary.damageTaken += direct;
      // Reflection is damage dealt back to the enemy, not an additional hit.
      summary.damageDealt += reflected;
      summary.healing += amount(-(turn.targetLastDamage ?? 0));
    }
    if (turn.event === FightEvents.CHANGE_ROLE) active[side]++;
  }
  return summary;
}
